import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { createCompany } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const INDUSTRIES = [
  'Technology',
  'Finance',
  'Healthcare',
  'Manufacturing',
  'Retail',
  'Energy',
  'Education',
  'Consulting',
  'Life Sciences',
  'Other',
] as const;

const companySchema = z.object({
  name: z.string().min(1, 'Company name is required').max(255),
  industry: z.string().max(100).optional(),
  website: z.string().max(500).optional(),
  address: z.string().optional(),
  phone: z.string().max(60).optional(),
  vatNumber: z.string().max(40).optional(),
  notes: z.string().optional(),
});

type CompanyFormData = z.infer<typeof companySchema>;

export function CreateCompanyPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [customIndustry, setCustomIndustry] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      industry: '',
      website: '',
      address: '',
      phone: '',
      vatNumber: '',
      notes: '',
    },
  });

  // Radix Select fields are not native inputs, so register them explicitly.
  useEffect(() => {
    register('industry');
  }, [register]);

  const createMutation = useMutation({
    mutationFn: createCompany,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      navigate(`/companies/${data.id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create company');
    },
  });

  const onSubmit = (data: CompanyFormData) => {
    setError(null);
    createMutation.mutate({
      name: data.name,
      industry: data.industry || null,
      website: data.website || null,
      address: data.address || null,
      phone: data.phone || null,
      vatNumber: data.vatNumber || null,
      notes: data.notes || null,
    });
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <Link
          to="/companies"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Companies
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-text-primary">Add New Company</h1>
        <p className="text-text-secondary mt-1">
          Create a new client company profile
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => setError('Please check the form for errors before saving.'))} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Company Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Acme Corporation"
                {...register('name')}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                {customIndustry ? (
                  <div className="flex gap-2">
                    <Input
                      id="industry"
                      placeholder="Enter custom industry"
                      {...register('industry')}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => { setCustomIndustry(false); setValue('industry', ''); }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Select
                    value={watch('industry') || ''}
                    onValueChange={(value) => {
                      if (value === '_none') setValue('industry', '', { shouldDirty: true, shouldValidate: true });
                      else if (value === '_custom') { setCustomIndustry(true); setValue('industry', '', { shouldDirty: true, shouldValidate: true }); }
                      else setValue('industry', value, { shouldDirty: true, shouldValidate: true });
                    }}
                  >
                    <SelectTrigger id="industry">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {INDUSTRIES.map((ind) => (
                        <SelectItem key={ind} value={ind}>
                          {ind}
                        </SelectItem>
                      ))}
                      <SelectItem value="_custom">+ Add Custom</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  placeholder="https://www.example.com"
                  {...register('website')}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              {/* Textarea, not Input: the address prints on tax invoices with
                  each line stacked — a single-line field flattens it. */}
              <Textarea
                id="address"
                placeholder={'123 Main Street\nCity\nPostal code'}
                rows={3}
                {...register('address')}
              />
              <p className="text-xs text-text-tertiary">
                One line per address line — it prints on invoices exactly as entered here.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="+27 12 345 6789"
                  {...register('phone')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vatNumber">VAT number</Label>
                <Input
                  id="vatNumber"
                  placeholder="e.g. 4670218645"
                  {...register('vatNumber')}
                />
                <p className="text-xs text-text-tertiary">
                  Prints in the invoice "Bill To" block.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this company..."
                rows={4}
                {...register('notes')}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to="/companies">Cancel</Link>
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Company'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
